"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const OTHER_VERSION = "other";

const WORKS = [
  { value: "The Bible", label: "The Bible" },
  { value: "The Quran", label: "The Quran" },
  { value: "The Torah", label: "The Torah" },
  { value: "The Book of Mormon", label: "The Book of Mormon" },
];

const VERSIONS = [
  "New International Version",
  "King James Version",
  "New King James Version",
  "English Standard Version",
  "New Revised Standard Version",
  "New American Standard Bible",
  "New Living Translation",
  "The Message",
  "The Qur'an (M. A. S. Abdel Haleem, Trans.)",
  "JPS Tanakh",
];

interface ScriptureInputs {
  work: string;
  version: string;
  book: string;
  chapter: string;
  verses: string;
  publisher: string;
  year: string;
}

interface RefPart {
  text: string;
  italic?: boolean;
}

interface StyleResult {
  style: string;
  refLabel: string;
  refParts: RefPart[];
  refPlain: string;
  inTextLabel: string;
  inText: string;
  note: string;
}

function passage(i: ScriptureInputs, separator: string): string {
  const base = `${i.book} ${i.chapter}`;
  if (i.verses.length === 0) {
    return base;
  }
  return `${base}${separator}${i.verses}`;
}

function buildApa(i: ScriptureInputs, strings: Strings): StyleResult {
  const year = i.year.length > 0 ? i.year : "n.d.";
  const publisherPart = i.publisher.length > 0 ? ` ${i.publisher}.` : "";
  return {
    style: strings.apaStyleName,
    refLabel: strings.referenceListLabel,
    refParts: [
      { text: i.version, italic: true },
      { text: `. (${year}).${publisherPart}` },
    ],
    refPlain: `${i.version}. (${year}).${publisherPart}`,
    inTextLabel: strings.inTextCitationLabel,
    inText: `(${i.version}, ${year}, ${passage(i, ":")})`,
    note: strings.apaNote,
  };
}

function buildMla(i: ScriptureInputs, strings: Strings): StyleResult {
  const tailItems = [i.version, i.publisher, i.year].filter(
    (item) => item.length > 0
  );
  const tail = tailItems.join(", ");
  return {
    style: strings.mlaStyleName,
    refLabel: strings.worksCitedLabel,
    refParts: [{ text: i.work, italic: true }, { text: `. ${tail}.` }],
    refPlain: `${i.work}. ${tail}.`,
    inTextLabel: strings.inTextCitationLabel,
    inText: `(${i.version}, ${passage(i, ".")})`,
    note: strings.mlaNote,
  };
}

function buildChicago(i: ScriptureInputs, strings: Strings): StyleResult {
  const noteText = `${passage(i, ":")} (${i.version}).`;
  return {
    style: strings.chicagoStyleName,
    refLabel: strings.footnoteLabel,
    refParts: [{ text: noteText }],
    refPlain: noteText,
    inTextLabel: strings.noteFormLabel,
    inText: noteText,
    note: strings.chicagoNote,
  };
}

function buildResults(i: ScriptureInputs, strings: Strings): StyleResult[] {
  return [buildApa(i, strings), buildMla(i, strings), buildChicago(i, strings)];
}

function FieldLabel({ htmlFor, text }: { htmlFor: string; text: string }) {
  return (
    <label className="font-medium text-foreground text-sm" htmlFor={htmlFor}>
      {text}
    </label>
  );
}

const INPUT_CLASS =
  "w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

/** English strings. A translated content file overrides these through `ui`. */
export const BIBLE_CITATION_GENERATOR_STRINGS = {
  otherTypeBelow: "Other (type below)",
  eGRevisedStandardVersion: "e.g. Revised Standard Version",
  eGJohn: "e.g. John",
  n16Or1618: "16 or 16-18",
  eGZondervan: "e.g. Zondervan",
  eG2011: "e.g. 2011",
  fillInTheVersionBook:
    "Fill in the version, book, and chapter to see your citations in APA, MLA, and Chicago.",
  yourCitations: "Your Citations",
  copied: "Copied!",
  copy: "Copy",
  howApaMlaAndChicago: "How APA, MLA, and Chicago Cite Scripture",
  apa7th: "APA 7th.",
  religiousWorksAreCitedLike:
    " Religious works are cited like books with no author: the version title (e.g.",
  newInternationalVersion: "New International Version",
  standsInTheTitlePosition:
    ") stands in the title position, followed by the year and publisher of the edition. In text, the passage replaces page numbers:",
  newInternationalVersion2011John:
    "(New International Version, 2011, John 3:16)",
  aColonSeparatesChapterAnd: ". A colon separates chapter and verse.",
  mla9th: "MLA 9th.",
  theWorksCitedEntryBegins:
    " The Works Cited entry begins with the italicized title of the edition (",
  theBible: "The Bible",
  thenTheVersionThePublisher:
    "), then the version, the publisher, and the year. In-text citations use a",
  period: "period",
  betweenChapterAndVerseNew:
    "between chapter and verse — (New International Version, John 3.16) — and name the version the first time you cite it.",
  chicagoNotesBibliography: "Chicago (notes-bibliography).",
  scriptureIsCitedInFootnotes:
    "Scripture is cited in footnotes or endnotes only, as book chapter:verse plus the version —",
  john316NewInternational: "John 3:16 (New International Version)",
  andIsUsuallyOmittedFrom: "— and is usually omitted from the bibliography.",
  religiousWorkLabel: "Religious work",
  versionTranslationLabel: "Version / translation",
  versionTranslationNameLabel: "Version or translation name",
  bookLabel: "Book",
  chapterLabel: "Chapter",
  versesLabel: "Verse(s)",
  publisherOptionalLabel: "Publisher (optional, for APA & MLA)",
  yearOptionalLabel: "Year of the edition (optional)",
  apaStyleName: "APA 7th edition",
  mlaStyleName: "MLA 9th edition",
  chicagoStyleName: "Chicago (notes-bibliography)",
  referenceListLabel: "Reference list",
  worksCitedLabel: "Works Cited",
  footnoteLabel: "Footnote",
  inTextCitationLabel: "In-text citation",
  noteFormLabel: "Note form",
  apaNote:
    "APA 7th cites religious works like books with no author: the version title stands in the title position and the passage (book chapter:verse) replaces page numbers in the in-text citation.",
  mlaNote:
    "MLA italicizes the title of the edition in Works Cited and uses a period — not a colon — between chapter and verse. Name the version in your first in-text citation; later citations can give just the passage.",
  chicagoNote:
    "Chicago cites scripture in footnotes or endnotes only, with book chapter:verse and the version. Religious works are usually omitted from the bibliography unless the edition itself is under discussion.",
} as const;

type Strings = typeof BIBLE_CITATION_GENERATOR_STRINGS;

export default function BibleCitationGeneratorWidget() {
  const [work, setWork] = useState(WORKS[0].value);
  const [versionChoice, setVersionChoice] = useState(VERSIONS[0]);
  const [customVersion, setCustomVersion] = useState("");
  const [book, setBook] = useState("");
  const [chapter, setChapter] = useState("");
  const [verses, setVerses] = useState("");
  const [publisher, setPublisher] = useState("");
  const [year, setYear] = useState("");
  const [copiedStyle, setCopiedStyle] = useState<string | null>(null);

  const strings = useToolUi(BIBLE_CITATION_GENERATOR_STRINGS);

  const version =
    versionChoice === OTHER_VERSION ? customVersion.trim() : versionChoice;
  const ready =
    version.length > 0 && book.trim().length > 0 && chapter.trim().length > 0;
  const results = ready
    ? buildResults(
        {
          work,
          version,
          book: book.trim(),
          chapter: chapter.trim(),
          verses: verses.trim(),
          publisher: publisher.trim(),
          year: year.trim(),
        },
        strings
      )
    : [];

  function handleCopy(style: string, text: string) {
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedStyle(style);
      setTimeout(() => setCopiedStyle(null), 2000);
    });
  }

  return (
    <>
      {/* Passage form */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel
              htmlFor="scripture-work"
              text={strings.religiousWorkLabel}
            />
            <select
              className={INPUT_CLASS}
              id="scripture-work"
              onChange={(e) => setWork(e.target.value)}
              value={work}
            >
              {WORKS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor="scripture-version"
              text={strings.versionTranslationLabel}
            />
            <select
              className={INPUT_CLASS}
              id="scripture-version"
              onChange={(e) => setVersionChoice(e.target.value)}
              value={versionChoice}
            >
              {VERSIONS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
              <option value={OTHER_VERSION}>{strings.otherTypeBelow}</option>
            </select>
          </div>
          {versionChoice === OTHER_VERSION && (
            <div className="space-y-2 sm:col-span-2">
              <FieldLabel
                htmlFor="scripture-custom-version"
                text={strings.versionTranslationNameLabel}
              />
              <input
                className={INPUT_CLASS}
                id="scripture-custom-version"
                onChange={(e) => setCustomVersion(e.target.value)}
                placeholder={strings.eGRevisedStandardVersion}
                type="text"
                value={customVersion}
              />
            </div>
          )}
          <div className="space-y-2">
            <FieldLabel htmlFor="scripture-book" text={strings.bookLabel} />
            <input
              className={INPUT_CLASS}
              id="scripture-book"
              onChange={(e) => setBook(e.target.value)}
              placeholder={strings.eGJohn}
              type="text"
              value={book}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <FieldLabel
                htmlFor="scripture-chapter"
                text={strings.chapterLabel}
              />
              <input
                className={INPUT_CLASS}
                id="scripture-chapter"
                onChange={(e) => setChapter(e.target.value)}
                placeholder="3"
                type="text"
                value={chapter}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="scripture-verses"
                text={strings.versesLabel}
              />
              <input
                className={INPUT_CLASS}
                id="scripture-verses"
                onChange={(e) => setVerses(e.target.value)}
                placeholder={strings.n16Or1618}
                type="text"
                value={verses}
              />
            </div>
          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor="scripture-publisher"
              text={strings.publisherOptionalLabel}
            />
            <input
              className={INPUT_CLASS}
              id="scripture-publisher"
              onChange={(e) => setPublisher(e.target.value)}
              placeholder={strings.eGZondervan}
              type="text"
              value={publisher}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor="scripture-year"
              text={strings.yearOptionalLabel}
            />
            <input
              className={INPUT_CLASS}
              id="scripture-year"
              onChange={(e) => setYear(e.target.value)}
              placeholder={strings.eG2011}
              type="text"
              value={year}
            />
          </div>
        </div>
        {!ready && (
          <p className="text-muted-foreground text-sm">
            {strings.fillInTheVersionBook}
          </p>
        )}
      </section>
      {/* Results */}
      {results.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.yourCitations}
          </h2>
          {results.map((result) => (
            <div
              className="space-y-3 rounded-lg border border-border bg-card p-5"
              key={result.style}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-medium text-foreground">{result.style}</h3>
                <button
                  className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                  onClick={() => handleCopy(result.style, result.refPlain)}
                  type="button"
                >
                  {copiedStyle === result.style ? strings.copied : strings.copy}
                </button>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  {result.refLabel}
                </p>
                <p className="text-foreground text-sm leading-relaxed">
                  {result.refParts.map((part) =>
                    part.italic ? (
                      <em key={part.text}>{part.text}</em>
                    ) : (
                      <span key={part.text}>{part.text}</span>
                    )
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  {result.inTextLabel}
                </p>
                <p className="text-foreground text-sm leading-relaxed">
                  {result.inText}
                </p>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {result.note}
              </p>
            </div>
          ))}
        </section>
      )}
      {/* Style rules */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howApaMlaAndChicago}
        </h2>
        <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <p>
            <strong className="text-foreground">{strings.apa7th}</strong>
            {strings.religiousWorksAreCitedLike}{" "}
            <em>{strings.newInternationalVersion}</em>
            {strings.standsInTheTitlePosition}{" "}
            <span className="font-medium text-foreground">
              {strings.newInternationalVersion2011John}
            </span>
            {strings.aColonSeparatesChapterAnd}
          </p>
          <p>
            <strong className="text-foreground">{strings.mla9th}</strong>
            {strings.theWorksCitedEntryBegins}
            <em>{strings.theBible}</em>
            {strings.thenTheVersionThePublisher}{" "}
            <span className="font-medium text-foreground">
              {strings.period}
            </span>{" "}
            {strings.betweenChapterAndVerseNew}
          </p>
          <p>
            <strong className="text-foreground">
              {strings.chicagoNotesBibliography}
            </strong>{" "}
            {strings.scriptureIsCitedInFootnotes}{" "}
            <span className="font-medium text-foreground">
              {strings.john316NewInternational}
            </span>{" "}
            {strings.andIsUsuallyOmittedFrom}
          </p>
        </div>
      </section>
    </>
  );
}
